import { useEffect, useState } from 'react';
import type { FoundryGameStateSnapshot } from '@volley/crucible-build-state/state-kernel.js';
import type { ProjectRef } from '@volley/crucible-ipc-types';
import { useCrucibleClient } from '../transport/context';
import { isProjectWorkspaceRouteActive } from '../transport/project-route';

type BoundSnapshot = {
  gameId: string;
  snapshot: FoundryGameStateSnapshot;
};

function shouldApplySnapshot(
  current: FoundryGameStateSnapshot | undefined,
  next: FoundryGameStateSnapshot,
): boolean {
  if (!current) return true;
  if (current.sourceRevision === next.sourceRevision) return false;
  return Date.parse(next.generatedAt) >= Date.parse(current.generatedAt);
}

export function useFoundryGameStateSnapshot(
  gameId: string | undefined,
  options: { enabled?: boolean; project?: ProjectRef } = {},
): FoundryGameStateSnapshot | undefined {
  const client = useCrucibleClient();
  const enabled = options.enabled ?? true;
  const projectPlacement = options.project?.placement;
  const projectGameId = options.project?.gameId;
  const [bound, setBound] = useState<BoundSnapshot | undefined>(undefined);

  useEffect(() => {
    if (!gameId || !enabled) {
      setBound(undefined);
      return;
    }

    setBound(undefined);
    let cancelled = false;

    const applySnapshot = (next: FoundryGameStateSnapshot) => {
      setBound((current) => {
        const previous =
          current !== undefined && current.gameId === gameId ? current.snapshot : undefined;
        if (!shouldApplySnapshot(previous, next)) return current;
        return { gameId, snapshot: next };
      });
    };

    const unsubscribePush = client.subscribe('game-state:update', (payload) => {
      if (cancelled) return;
      if (payload.gameId !== gameId) return;
      applySnapshot(payload.snapshot);
    });

    // Restart the server-side poll loop by (re-)issuing `game-state:subscribe`.
    // `applySnapshot` is guarded by `shouldApplySnapshot`, so a snapshot that
    // races a fresher push can never regress the State tab — this doubles as the
    // stale-overwrite guard for the reconnect re-subscribe below.
    const runSubscribe = () => {
      void client
        .invoke('game-state:subscribe', { gameId })
        .then((current) => {
          if (cancelled) return;
          applySnapshot(current);
        })
        .catch(() => {
          // Snapshot subscription failures degrade to the legacy Workspace
          // derivation until main can answer again.
        });
    };
    runSubscribe();

    // crucible#3011: a websocket reconnect mints a NEW cloud session and the
    // server tears down this game's poll loop (keyed by the old sessionId). The
    // client-side push subscription above survives the reconnect, but nothing
    // would restart the server loop — so the State tab would keep a stale
    // snapshot until the Workspace remounts. Re-issue `game-state:subscribe` on
    // the client's reconnect signal (Electron's is an inert no-op). No push
    // re-registration is needed; only the server loop must be restarted.
    const unsubscribeReconnect = client.subscribeReconnect?.(() => {
      if (cancelled) return;
      runSubscribe();
    });

    return () => {
      cancelled = true;
      unsubscribePush();
      unsubscribeReconnect?.();
      const project =
        projectPlacement && projectGameId
          ? { placement: projectPlacement, gameId: projectGameId }
          : undefined;
      if (!project || isProjectWorkspaceRouteActive(project)) {
        void client.invoke('game-state:unsubscribe', { gameId }).catch(() => {
          // Best effort. Route-leave cleanup is main-owned because navigation
          // authority is revoked before passive React cleanup runs.
        });
      }
    };
  }, [client, enabled, gameId, projectGameId, projectPlacement]);

  // Pin the returned snapshot to the active gameId. useEffect clears state
  // post-commit, so the first render after a route change would otherwise
  // still expose the previous game's kernel snapshot to Workspace.
  return bound !== undefined && bound.gameId === gameId ? bound.snapshot : undefined;
}
